import { useWavesurfer } from '@wavesurfer/react';
import { useEffect, useMemo, useRef, type RefObject } from 'react';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';
import { createPlaceholderAudioUrl } from './placeholderAudio';

export interface TrimWavesurferOptions {
  containerRef: RefObject<HTMLDivElement | null>;
  trackDurationMs: number;
  startMs: number;
  endMs: number;
  minDurationMs: number;
  maxDurationMs: number;
  /** Full-track audio URL. When absent, a generated placeholder track is used. */
  audioUrl?: string;
  waveColor: string;
  progressColor: string;
  cursorColor: string;
  regionColor: string;
  onChange: (range: { startMs: number; endMs: number }) => void;
  /** Called whenever audible playback starts (initial play and resume). */
  onPlayStart?: () => void;
}

export interface TrimWavesurfer {
  /** Whether the wavesurfer instance is ready. */
  isReady: boolean;
  /** Whether the wavesurfer instance is currently playing. */
  isPlaying: boolean;
  /** Toggles playback (play/pause) of the wavesurfer instance. */
  toggle: () => void;
  /** Seeks the wavesurfer instance to a specific time in milliseconds. */
  seekTo: (ms: number) => void;
}

/**
 * Wraps wavesurfer.js + its Regions plugin for the trim editor: a single
 * draggable/resizable region over [startMs, endMs], playback looping within
 * that region, and a fallback to a generated placeholder track if there's no
 * preview URL or the real one fails to load. Mirrors the pattern already
 * used by `TrackPlayer/Scrubber.tsx`, extended with the placeholder fallback
 * so the editor never needs a separate fake-playback code path.
 */
export function useTrimWavesurfer({
  containerRef,
  trackDurationMs,
  startMs,
  endMs,
  minDurationMs,
  maxDurationMs,
  audioUrl,
  waveColor,
  progressColor,
  cursorColor,
  regionColor,
  onChange,
  onPlayStart,
}: TrimWavesurferOptions): TrimWavesurfer {
  const regionsPlugin = useMemo(() => RegionsPlugin.create(), []);
  const placeholderUrlRef = useRef<string | null>(null);
  const usingPlaceholderRef = useRef(!audioUrl);

  const resolvePlaceholderUrl = () => {
    if (!placeholderUrlRef.current) {
      placeholderUrlRef.current = createPlaceholderAudioUrl(trackDurationMs);
    }
    return placeholderUrlRef.current;
  };

  useEffect(() => {
    return () => {
      if (placeholderUrlRef.current) {
        URL.revokeObjectURL(placeholderUrlRef.current);
        placeholderUrlRef.current = null;
      }
    };
  }, [trackDurationMs]);

  usingPlaceholderRef.current = !audioUrl;
  const url = audioUrl ?? resolvePlaceholderUrl();

  const wavesurferConfig = useMemo(
    () => ({
      container: containerRef,
      height: 88,
      waveColor,
      progressColor,
      cursorColor,
      cursorWidth: 1.5,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      plugins: [regionsPlugin],
      url,
    }),
    [containerRef, regionsPlugin, url, waveColor, progressColor, cursorColor],
  );

  const wavesurfer = useWavesurfer(wavesurferConfig);

  // Fall back to the placeholder track if the real preview URL fails to load.
  useEffect(() => {
    const instance = wavesurfer.wavesurfer;
    if (!instance || usingPlaceholderRef.current) return;
    return instance.on('error', () => {
      usingPlaceholderRef.current = true;
      instance.load(resolvePlaceholderUrl());
    });
  }, [wavesurfer.wavesurfer]);

  // Sync startMs/endMs (drag, resize, or typed-in time) onto the region.
  useEffect(() => {
    if (!wavesurfer.isReady) return;
    const startSec = startMs / 1000;
    const endSec = endMs / 1000;
    const [region] = regionsPlugin.getRegions();
    if (!region) {
      regionsPlugin.addRegion({
        start: startSec,
        end: endSec,
        drag: true,
        resize: true,
        color: regionColor,
        minLength: minDurationMs / 1000,
        maxLength: maxDurationMs / 1000,
      });
      return;
    }
    if (region.start !== startSec || region.end !== endSec) {
      region.setOptions({ start: startSec, end: endSec });
    }
  }, [
    regionsPlugin,
    wavesurfer.isReady,
    startMs,
    endMs,
    minDurationMs,
    maxDurationMs,
    regionColor,
  ]);

  // Drag/resize on the region reports back through the same onChange
  // contract TrimEditor already exposes to its parent.
  useEffect(() => {
    if (!wavesurfer.isReady) return;
    return regionsPlugin.on('region-update', (region) => {
      onChange({
        startMs: Math.round(region.start * 1000),
        endMs: Math.round(region.end * 1000),
      });
    });
  }, [regionsPlugin, wavesurfer.isReady, onChange]);

  // A drag/resize just finished — if it moved the region out from under a
  // static playhead, snap back into the new bounds. `getCurrentTime()` is
  // the live instance value; the hook's `currentTime` state can lag behind
  // inside an event handler like this one.
  useEffect(() => {
    if (!wavesurfer.isReady) return;
    return regionsPlugin.on('region-updated', (region) => {
      const now = wavesurfer.wavesurfer?.getCurrentTime();
      if (now !== undefined && (now < region.start || now > region.end)) {
        wavesurfer.wavesurfer?.setTime(region.start);
      }
    });
  }, [regionsPlugin, wavesurfer.isReady, wavesurfer.wavesurfer]);

  // Loop playback within the region instead of running past it. No need to
  // call `.play()` again here — setting the time doesn't interrupt playback
  // that's already underway.
  useEffect(() => {
    if (!wavesurfer.isReady) return;
    return regionsPlugin.on('region-out', (region) => {
      wavesurfer.wavesurfer?.setTime(region.start);
    });
  }, [regionsPlugin, wavesurfer.isReady, wavesurfer.wavesurfer]);

  const toggle = () => {
    const instance = wavesurfer.wavesurfer;
    if (!instance) return;
    if (wavesurfer.isPlaying) {
      instance.pause();
      return;
    }
    const [region] = regionsPlugin.getRegions();
    const now = instance.getCurrentTime();
    if (region && (now < region.start || now > region.end)) {
      instance.setTime(region.start);
    }
    onPlayStart?.();
    instance.play();
  };

  const seekTo = (ms: number) => {
    wavesurfer.wavesurfer?.setTime(ms / 1000);
  };

  return {
    isReady: wavesurfer.isReady,
    isPlaying: wavesurfer.isPlaying,
    toggle,
    seekTo,
  };
}
